import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../ConfirmDialog.jsx';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';
import { platformLabel } from '../../lib/operations.js';
import '../../styles/social-metrics.css';

// GH-939: the in-app confirmation before the optional statistics-permission
// consent start. Shared by the TikTok statistics banner and Settings →
// Connected social accounts so both ask the same question. Open while
// `account` is set; the caller starts the consent on confirm. Portalled to
// <body> so an ancestor's transform/overflow never traps the fixed scrim.
export default function MetricsConsentDialog({ account, confirming = false, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const latest = useRef({ confirming, onCancel });
  latest.current = { confirming, onCancel };
  const open = Boolean(account);

  // Focus moves into the dialog on open, Tab stays inside it and focus returns
  // to the trigger on close (same pattern as PlanSwitchReviewDialog). A page
  // restored from the back-forward cache after the OAuth redirect closes it.
  useEffect(() => {
    if (!open) return undefined;
    const dialog = wrapperRef.current?.querySelector('[role="dialog"]');
    const previousFocus = document.activeElement;
    if (dialog) dialog.tabIndex = -1;
    dialog?.querySelector('.btn-ghost')?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (!latest.current.confirming) latest.current.onCancel?.();
      } else if (event.key === 'Tab' && dialog) {
        const buttons = [...dialog.querySelectorAll('button:not([disabled])')];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!first || !dialog.contains(document.activeElement)
          || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          if (first) (event.shiftKey ? last : first).focus();
          else dialog.focus();
        }
      }
    };
    const onShow = (event) => { if (event.persisted) latest.current.onCancel?.(); };
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('pageshow', onShow);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pageshow', onShow);
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [open]);

  if (!account) return null;
  const key = String(account.platform || '').toLowerCase();
  const platform = platformLabel(account.platform);
  const mark = BrandIcons[key]?.({ size: 20 }) || <I.info size={18} />;

  return createPortal((
    <div className="mcd" data-metrics-consent-dialog={key} ref={wrapperRef}>
      <ConfirmDialog
        open
        tone="info"
        icon={mark}
        title={t('socialMetrics.consentTitle', { platform })}
        description={t('socialMetrics.consentDescription', { platform, account: account.displayName || platform })}
        confirmLabel={t('socialMetrics.consentContinue', { platform })}
        confirmingLabel={t('postingConnectConfirm.confirming', { provider: platform })}
        cancelLabel={t('postingConnectConfirm.cancel')}
        confirming={confirming}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  ), document.body);
}
